import React from 'react';
import PropTypes from 'prop-types';

/**
 * A component that represents a link.
 * @param {Object} props - The props.
 * @param {boolean} props.disabled - Whether the link is disabled.
 * @param {string} props.download - The download.
 * @param {string} props.href - The href.
 * @param {string} props.iconEnd - The end icon.
 * @param {string} props.iconFlipRtl - The icon flip RTL.
 * @param {string} props.iconStart - The start icon.
 * @param {string} props.rel - The rel.
 * @param {string} props.target - The target.
 * @param {string} props.className - The class name.
 * @param {React.ReactNode} props.children - The children.
 * @returns {JSX.Element} The component.
 * @example
 * <CalciteLink
 * disabled={false}
 * download="Download"
 * href="https://example.com"
 * iconEnd="circle"
 * iconFlipRtl="both"
 * iconStart="circle"
 * rel="noopener"
 * target="_blank"
 * className="Class"
 * >
 * Children
 * </CalciteLink>
 **/
const CalciteLink = React.forwardRef(
  ({disabled, download, href, iconEnd, iconFlipRtl, iconStart, rel, target, className, children, ...rest}, ref) => {
    return (
      <calcite-link
        ref={ref}
        class={className || undefined}
        disabled={disabled == null ? undefined : disabled}
        download={download == null ? undefined : download}
        href={href || undefined}
        icon-end={iconEnd || undefined}
        icon-flip-rtl={iconFlipRtl || undefined}
        icon-start={iconStart || undefined}
        rel={rel || undefined}
        target={target || undefined}
        {...rest}
      >
        {children}
      </calcite-link>
    );
  },
);

CalciteLink.propTypes = {
  disabled: PropTypes.bool,
  download: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  href: PropTypes.string,
  iconEnd: PropTypes.string,
  iconFlipRtl: PropTypes.oneOf(['both', 'start', 'end']),
  iconStart: PropTypes.string,
  rel: PropTypes.string,
  target: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default CalciteLink;
